/** API utilities for loading domain schemas */

import { API_HOST } from "../../constants";
import type { Dataset } from "../../types";

export interface LifecycleStep {
  step: number;
  title: string;
  description: string;
  status: string;
}

export interface DomainSchema {
  name: string;
  description: string;
  detail_info?: string;
  lifecycle_steps?: LifecycleStep[];
  tables: Dataset[];
  relations: Array<[string, string]>;
}

async function loadDomainSchema(domainName: string): Promise<DomainSchema | null> {
  try {
    const response = await fetch(`${API_HOST}/api/content/schemas/domains/${domainName}`);

    if (!response.ok) {
      console.error(`Failed to load domain ${domainName}: ${response.statusText}`);
      return null;
    }

    const data = await response.json();

    const tables: Dataset[] = (data.tables || []).map((table: any) => ({
      name: table.name,
      description: table.description || "",
      schema: table.schema,
      layer: table.layer,
      ddl: table.ddl,
      columns: (table.columns || []).map((col: any) => ({
        name: col.name,
        type: col.type,
        description: col.description || "",
      })),
    }));

    const relations: Array<[string, string]> = (data.relations || []).map((rel: any) => {
      if (Array.isArray(rel) && rel.length === 2) {
        return [rel[0], rel[1]] as [string, string];
      } else if (rel.from && rel.to) {
        return [rel.from, rel.to] as [string, string];
      }
      return null;
    }).filter((r: any): r is [string, string] => r !== null);

    return {
      name: data.name || domainName,
      description: data.description || "",
      detail_info: data.detail_info,
      lifecycle_steps: data.lifecycle_steps,
      tables,
      relations,
    };
  } catch (error) {
    console.error(`Error loading domain ${domainName}:`, error);
    return null;
  }
}

async function loadDomainNames(): Promise<string[]> {
  try {
    const response = await fetch(`${API_HOST}/api/content/schemas/domains`);

    if (!response.ok) {
      console.error(`Failed to load domain names: ${response.statusText}`);
      return [];
    }

    const data = await response.json();

    if (Array.isArray(data)) {
      return data.map((d: any) => d.name || d);
    }

    if (data.domains && Array.isArray(data.domains)) {
      return data.domains.map((d: any) => d.name || d);
    }

    return [];
  } catch (error) {
    console.error("Error loading domain names:", error);
    return [];
  }
}

/**
 * Loads all available domain schemas from the API
 */
export async function loadAllDomainSchemas(): Promise<DomainSchema[]> {
  try {
    const domainNames = await loadDomainNames();
    
    if (domainNames.length === 0) {
      return [];
    }
    
    const schemas = await Promise.all(
      domainNames.map(name => loadDomainSchema(name))
    );
    
    return schemas.filter((s): s is DomainSchema => s !== null);
  } catch (error) {
    console.error("Error loading all domain schemas:", error);
    return [];
  }
}


